import cn from 'classnames';
import Script from 'next/script';
import { useRef } from 'react';

import { useOnMount } from 'src/hooks/useOnMount';

import { setJumpLinkAttributes } from 'src/utils/jumpLinks';

import Cta from 'components/BaseHelpers/Cta';
import RichText from 'components/BaseHelpers/RichText';
import Text from 'components/BaseHelpers/Text';

import { ComponentProps } from '../../../types/component-props';
import { CoreSocialMediaEmbedsData } from '../../../types/components';

import styles from './CoreSocialMediaEmbeds.module.scss';

export type CoreSocialMediaEmbedsProps = ComponentProps & CoreSocialMediaEmbedsData;

type WindowWithEmbedSocial = Window & {
  standardLoad?: (el: Element[]) => void;
};

const CoreSocialMediaEmbeds = (props: CoreSocialMediaEmbedsProps): JSX.Element => {
  const { heading, description, embedCode, cta, jumpLinkLabel } = props.fields;
  const dataRef = embedCode.match(/data-ref="([^"]+)"/)?.[1] || '';
  const ref = useRef<HTMLDivElement>(null);

  useOnMount(() => {
    const embedWindow = window as WindowWithEmbedSocial;
    if (!ref.current || !dataRef || !embedWindow.standardLoad) return;
    setTimeout(() => {
      if (ref.current) {
        embedWindow.standardLoad?.([ref.current]);
      }
    }, 200);
  });

  if (!dataRef) {
    return <></>;
  }

  return (
    <div className={cn(styles.main, 'container-10')} {...setJumpLinkAttributes(jumpLinkLabel)}>
      <div className={styles.left}>
        <Text tag="h2" className={cn(styles.heading, 'heading-h3')} field={heading} />
        <RichText className={cn(styles.description, 'body-med')} field={description} />
        <Cta tag="a" href={cta?.href} openNewTab={cta?.target === '_blank'} className={styles.cta}>
          {cta?.label}
        </Cta>
      </div>
      <div className={styles.right}>
        <div className="embedsocial-hashtag" data-ref={dataRef} ref={ref} />
        <Script src="https://embedsocial.com/cdn/ht.js" strategy="afterInteractive" />
      </div>
    </div>
  );
};

export default CoreSocialMediaEmbeds;
